import { useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { FaIcon } from 'client/js/util/layout_utils';

const TimeRangePicker = ({ value, onChange, error, className = '', maxDays = 7 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startTime, setStartTime] = useState('now-1h');
  const [endTime, setEndTime] = useState('now');
  const [tempError, setTempError] = useState('');
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const dropdownRef = useRef(null);

  // Quick preset options - filter based on maxDays
  const allPresets = [
    { label: 'Letzte 10 Minuten', start: 'now-10m', end: 'now', maxDays: 0.01 },
    { label: 'Letzte 30 Minuten', start: 'now-30m', end: 'now', maxDays: 0.02 },
    { label: 'Letzte 1 Stunde', start: 'now-1h', end: 'now', maxDays: 0.04 },
    { label: 'Letzte 2 Stunden', start: 'now-2h', end: 'now', maxDays: 0.08 },
    { label: 'Letzte 6 Stunden', start: 'now-6h', end: 'now', maxDays: 0.25 },
    { label: 'Letzte 12 Stunden', start: 'now-12h', end: 'now', maxDays: 0.5 },
    { label: 'Letzte 24 Stunden', start: 'now-24h', end: 'now', maxDays: 1 },
    { label: 'Letzte 2 Tage', start: 'now-2d', end: 'now', maxDays: 2 },
    { label: 'Letzte 7 Tage', start: 'now-7d', end: 'now', maxDays: 7 },
    { label: 'Letzte 30 Tage', start: 'now-30d', end: 'now', maxDays: 30 },
    { label: 'Letzte 90 Tage', start: 'now-90d', end: 'now', maxDays: 90 },
  ];
  
  const presets = allPresets.filter(preset => preset.maxDays <= maxDays);

  // Parse time expression like "now-1h", "now-30m", "2024-01-01T10:00"
  const parseTimeExpression = (expr) => {
    if (!expr) return null;

    try {
      // Handle "now" expressions
      if (expr === 'now') {
        return dayjs();
      }

      if (expr.startsWith('now-')) {
        const timeStr = expr.substring(4); // Remove "now-"
        const now = dayjs();

        // Parse different units: 1h, 30m, 2d, etc.
        const match = timeStr.match(/^(\d+)([mhd])$/);
        if (match) {
          const [, amount, unit] = match;
          const unitMap = { m: 'minute', h: 'hour', d: 'day' };
          return now.subtract(parseInt(amount), unitMap[unit]);
        }

        // Parse complex expressions like "6h30m"
        const complexMatch = timeStr.match(/^(?:(\d+)d)?(?:(\d+)h)?(?:(\d+)m)?$/);
        if (complexMatch) {
          const [, days, hours, minutes] = complexMatch;
          let result = now;
          if (days) result = result.subtract(parseInt(days), 'day');
          if (hours) result = result.subtract(parseInt(hours), 'hour');
          if (minutes) result = result.subtract(parseInt(minutes), 'minute');
          return result;
        }
      }

      // Handle absolute timestamps
      const parsed = dayjs(expr);
      if (parsed.isValid()) {
        return parsed;
      }

      return null;
    } catch (e) {
      return null;
    }
  };

  // Get current range text for button display
  const getCurrentRangeText = () => {
    if (!value) return 'Zeit-Filter wählen';

    // Parse the current value to determine the time range
    const startParsed = parseTimeExpression(startTime);
    const endParsed = parseTimeExpression(endTime);

    // Check if it matches a preset pattern (now-Xh, now-Xm, now-Xd format)
    if (startTime.startsWith('now-') && endTime === 'now') {
      const timeStr = startTime.substring(4); // Remove "now-"

      // Handle simple patterns like 5m, 15m, 30m, 1h, 3h, etc.
      const match = timeStr.match(/^(\d+)([mhd])$/);
      if (match) {
        const [, amount, unit] = match;
        const num = parseInt(amount);

        if (unit === 'm') {
          return num === 1 ? 'Letzte Minute' : `Letzte ${num} Minuten`;
        } else if (unit === 'h') {
          return num === 1 ? 'Letzte Stunde' : `Letzte ${num} Stunden`;
        } else if (unit === 'd') {
          return num === 1 ? 'Letzter Tag' : `Letzte ${num} Tage`;
        }
      }

      // Handle complex patterns like 6h30m - just show the original format
      return `Letzte ${timeStr}`;
    }

    // For custom absolute times, show formatted start and end
    if (startParsed && endParsed) {
      const startFormatted = startParsed.format('DD.MM.YYYY HH:mm');
      const endFormatted = endParsed.format('DD.MM.YYYY HH:mm');
      return `${startFormatted} - ${endFormatted}`;
    }

    return 'Ungültiger Zeitbereich';
  };

  // Initialize state from value
  useEffect(() => {
    if (value && value.includes('|')) {
      const [start, end] = value.split('|');
      setStartTime(start);
      setEndTime(end);
    } else if (!value) {
      setStartTime('now-1h');
      setEndTime('now');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowStartPicker(false);
        setShowEndPicker(false);
      }
    };

    if (isOpen || showStartPicker || showEndPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, showStartPicker, showEndPicker]);

  const applyTimeRange = () => {
    setTempError('');

    // Validate time expressions
    const startParsed = parseTimeExpression(startTime);
    const endParsed = parseTimeExpression(endTime);
    const maxDaysAgo = dayjs().subtract(maxDays, 'day');
    const dayText = maxDays === 1 ? 'Tag' : 'Tage';

    if (!startParsed) {
      setTempError('Ungültige Start-Zeit');
      return;
    }

    if (!endParsed) {
      setTempError('Ungültige End-Zeit');
      return;
    }

    if (startParsed.isAfter(endParsed)) {
      setTempError('Start-Zeit muss vor End-Zeit liegen');
      return;
    }

    // Check max days restriction
    if (startParsed.isBefore(maxDaysAgo, 'minute')) {
      setTempError(`Start-Zeit kann nicht älter als ${maxDays} ${dayText} sein`);
      return;
    }

    if (endParsed.isBefore(maxDaysAgo, 'minute')) {
      setTempError(`End-Zeit kann nicht älter als ${maxDays} ${dayText} sein`);
      return;
    }

    // Apply the filter - send both from_time and to_time
    const timeRange = {
      from_time: startParsed.format('YYYY-MM-DDTHH:mm'),
      to_time: endParsed.format('YYYY-MM-DDTHH:mm')
    };
    onChange(timeRange);
    setIsOpen(false);
  };

  const applyPreset = (preset) => {
    setStartTime(preset.start);
    setEndTime(preset.end);
    setTempError('');

    // Auto-apply preset
    const startParsed = parseTimeExpression(preset.start);
    const endParsed = parseTimeExpression(preset.end);
    const maxDaysAgo = dayjs().subtract(maxDays, 'day');
    const dayText = maxDays === 1 ? 'Tag' : 'Tage';

    if (startParsed && endParsed) {
      // Check max days restriction for presets too
      if (startParsed.isBefore(maxDaysAgo, 'minute')) {
        setTempError(`Start-Zeit kann nicht älter als ${maxDays} ${dayText} sein`);
        return;
      }

      const timeRange = {
        from_time: startParsed.format('YYYY-MM-DDTHH:mm'),
        to_time: endParsed.format('YYYY-MM-DDTHH:mm')
      };
      onChange(timeRange);
    }
    setIsOpen(false);
  };

  const clearFilter = () => {
    setStartTime('now-1h');
    setEndTime('now');
    setTempError('');
    onChange(null);
    setIsOpen(false);
  };

  return (
    <div className={`time-range-picker ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`btn btn-sm border bg-white time-range-button ${error ? 'is-invalid' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaIcon name="clock-o" className="time-range-icon" />
        <span className="time-range-text">{getCurrentRangeText()}</span>
        <FaIcon name="chevron-down" className="time-range-chevron" />
      </button>

      {isOpen && (
        <div className="time-range-dropdown">
          <div className="time-range-content">
            {/* Left side: Custom time inputs */}
            <div className="time-range-custom">
              <h6>Zeitbereich</h6>

              <div className="form-group">
                <label>Von:</label>
                <div className="time-input-with-picker">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    placeholder="z.B. now-1h, now-30m, 2024-01-01 10:00"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm picker-btn"
                    onClick={() => setShowStartPicker(!showStartPicker)}
                    title="Datum/Zeit-Picker öffnen"
                  >
                    <FaIcon name="calendar" />
                  </button>

                  {showStartPicker && (
                    <div className="datetime-picker-popover">
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        onChange={(e) => {
                          setStartTime(e.target.value);
                          setShowStartPicker(false);
                        }}
                        min={dayjs().subtract(maxDays, 'day').format('YYYY-MM-DDTHH:mm')}
                        max={dayjs().format('YYYY-MM-DDTHH:mm')}
                        defaultValue={
                          parseTimeExpression(startTime)?.format('YYYY-MM-DDTHH:mm') || ''
                        }
                      />
                    </div>
                  )}
                </div>
                <small className="form-text text-muted">
                  Beispiele: now-1h, now-30m, now-2d, 2024-01-01 10:00
                </small>
              </div>

              <div className="form-group">
                <label>Bis:</label>
                <div className="time-input-with-picker">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    placeholder="z.B. now, 2024-01-01 11:00"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm picker-btn"
                    onClick={() => setShowEndPicker(!showEndPicker)}
                    title="Datum/Zeit-Picker öffnen"
                  >
                    <FaIcon name="calendar" />
                  </button>

                  {showEndPicker && (
                    <div className="datetime-picker-popover">
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        onChange={(e) => {
                          setEndTime(e.target.value);
                          setShowEndPicker(false);
                        }}
                        min={dayjs().subtract(maxDays, 'day').format('YYYY-MM-DDTHH:mm')}
                        max={dayjs().format('YYYY-MM-DDTHH:mm')}
                        defaultValue={
                          parseTimeExpression(endTime)?.format('YYYY-MM-DDTHH:mm') || ''
                        }
                      />
                    </div>
                  )}
                </div>
              </div>

              {tempError && (
                <div className="alert alert-danger alert-sm">{tempError}</div>
              )}

              <div className="time-range-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={applyTimeRange}
                >
                  Anwenden
                </button>
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={clearFilter}
                >
                  Zurücksetzen
                </button>
              </div>
            </div>

            {/* Right side: Quick presets */}
            <div className="time-range-presets">
              <h6>Schnellauswahl</h6>
              <div className="preset-buttons">
                {presets.map((preset, index) => (
                  <button
                    key={index}
                    type="button"
                    className="btn preset-btn"
                    onClick={() => applyPreset(preset)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

TimeRangePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  className: PropTypes.string,
  maxDays: PropTypes.number
};

export default TimeRangePicker;
